import type { ReactNode } from "react";
import { Checkbox } from "@pack-it-up/ui-web";
import type { CategoryId } from "@pack-it-up/shared";
import type { CategoryTickState } from "@pack-it-up/viewmodel";
import { syncEntryKey } from "@pack-it-up/core";
import type { SyncCollection, SyncEntry } from "@pack-it-up/core";

// ── T28 sync mode (Doc 13 §1) ────────────────────────────────────────────────
// Sync is a *mode over the existing list*, not a separate diff view: the page hands the
// lists this handle and every row's leading slot becomes a tick. The page owns the
// `templateSyncStore`; this is the read/act surface the lists need, nothing more.
export interface SyncListMode {
  // The draft entry for a trip row, by collection + id. Absent ⇒ render nothing.
  entryFor: (collection: SyncCollection, tripRowId: string) => SyncEntry | undefined;
  isChecked: (key: string) => boolean;
  toggle: (key: string) => void;
  categoryTickState: (key: string) => CategoryTickState;
  toggleCategory: (key: string) => void;
}

// A row's tick. Keys always come from `syncEntryKey`, which is what the planner reads back.
export const SyncRowTick = ({ mode, entry, name }: { mode: SyncListMode; entry: SyncEntry; name: string }): ReactNode => {
  const key = syncEntryKey(entry);
  return <Checkbox variant="sync" checked={mode.isChecked(key)} onChange={() => mode.toggle(key)} aria-label={`Sync ${name}`} />;
};

// A category's tri-state tick — a convenience that cascades its rows' ticks (Doc 13 §5),
// never an independent delete switch.
export const SyncCategoryTick = ({ mode, categoryId, name }: { mode: SyncListMode; categoryId: CategoryId; name: string }): ReactNode => {
  const entry = mode.entryFor("categories", categoryId);
  if (entry === undefined) return null;
  const key = syncEntryKey(entry);
  const state = mode.categoryTickState(key);
  return (
    <Checkbox
      variant="sync"
      checked={state === "all"}
      indeterminate={state === "some"}
      onChange={() => mode.toggleCategory(key)}
      aria-label={`Sync everything in ${name}`}
    />
  );
};
